import { Label, TextInput, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@c2fo/liquidity';
import { ArchiveAction, type PreDeliveryAction, type PostDeliveryAction } from '../../api/shared';

interface DeliveryActionsEditorProps {
  preDeliveryAction?: PreDeliveryAction;
  postDeliveryAction?: PostDeliveryAction;
  onPreDeliveryActionChange: (action: PreDeliveryAction | undefined) => void;
  onPostDeliveryActionChange: (action: PostDeliveryAction | undefined) => void;
}

const ACTION_OPTIONS = [
  { value: ArchiveAction.ARCHIVE_ACTION_UNSPECIFIED, label: 'None' },
  { value: ArchiveAction.ARCHIVE_ACTION_MOVE, label: 'Move to archive' },
  { value: ArchiveAction.ARCHIVE_ACTION_DELETE, label: 'Delete' },
];

const getActionLabel = (action?: ArchiveAction): string => {
  switch (action) {
    case ArchiveAction.ARCHIVE_ACTION_MOVE:
      return 'Move to archive';
    case ArchiveAction.ARCHIVE_ACTION_DELETE:
      return 'Delete';
    default:
      return 'None';
  }
};

interface ActionSectionProps {
  label: string;
  description: string;
  action?: PreDeliveryAction | PostDeliveryAction;
  onChange: (action: PreDeliveryAction | PostDeliveryAction | undefined) => void;
}

const ActionSection: React.FC<ActionSectionProps> = ({ label, description, action, onChange }) => {
  const currentAction = action?.action ?? ArchiveAction.ARCHIVE_ACTION_UNSPECIFIED;
  const showArchivePath = currentAction === ArchiveAction.ARCHIVE_ACTION_MOVE;

  const handleActionChange = (value: string) => {
    const newAction = value as ArchiveAction;
    if (newAction === ArchiveAction.ARCHIVE_ACTION_UNSPECIFIED) {
      onChange(undefined);
    } else {
      onChange({
        action: newAction,
        archivePath: newAction === ArchiveAction.ARCHIVE_ACTION_MOVE ? action?.archivePath : undefined,
      });
    }
  };

  const handleArchivePathChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({
      action: currentAction,
      archivePath: e.target.value,
    });
  };

  return (
    <div className="space-y-3">
      <div>
        <Label className="block text-sm font-medium text-gray-700">{label}</Label>
        <p className="text-xs text-gray-500 mb-2">{description}</p>
        <Select value={currentAction} onValueChange={handleActionChange}>
          <SelectTrigger className="w-full">
            <SelectValue>{getActionLabel(currentAction)}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            {ACTION_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {showArchivePath && (
        <div>
          <Label className="block text-sm font-medium text-gray-700">Archive Path</Label>
          <TextInput
            value={action?.archivePath ?? ''}
            onChange={handleArchivePathChange}
            placeholder="/archive/{date}"
            className="w-full"
          />
          <p className="text-xs text-gray-500 mt-1">
            Path where files will be moved. Supports {'{'}
            {'{'}date{'}'}
            {'}'} placeholder.
          </p>
        </div>
      )}
    </div>
  );
};

export const DeliveryActionsEditor: React.FC<DeliveryActionsEditorProps> = ({
  preDeliveryAction,
  postDeliveryAction,
  onPreDeliveryActionChange,
  onPostDeliveryActionChange,
}) => {
  return (
    <div className="space-y-6">
      <h3 className="text-sm font-semibold text-gray-900 border-b pb-2">Delivery Actions</h3>

      <ActionSection
        label="Pre-Delivery Action"
        description="Action to perform on source files before delivery starts"
        action={preDeliveryAction}
        onChange={onPreDeliveryActionChange}
      />

      <ActionSection
        label="Post-Delivery Action"
        description="Action to perform on source files after successful delivery"
        action={postDeliveryAction}
        onChange={onPostDeliveryActionChange}
      />
    </div>
  );
};

export const DeliveryActionsDisplay: React.FC<{
  preDeliveryAction?: PreDeliveryAction;
  postDeliveryAction?: PostDeliveryAction;
}> = ({ preDeliveryAction, postDeliveryAction }) => {
  const hasPreAction =
    preDeliveryAction?.action && preDeliveryAction.action !== ArchiveAction.ARCHIVE_ACTION_UNSPECIFIED;
  const hasPostAction =
    postDeliveryAction?.action && postDeliveryAction.action !== ArchiveAction.ARCHIVE_ACTION_UNSPECIFIED;

  if (!hasPreAction && !hasPostAction) {
    return <p className="text-sm text-gray-500 italic">No delivery actions configured</p>;
  }

  return (
    <div className="space-y-3">
      {hasPreAction && (
        <div>
          <p className="text-xs text-gray-500">Pre-Delivery</p>
          <p className="text-sm text-gray-900">
            {getActionLabel(preDeliveryAction?.action)}
            {preDeliveryAction?.archivePath && (
              <span className="text-gray-600"> → {preDeliveryAction.archivePath}</span>
            )}
          </p>
        </div>
      )}
      {hasPostAction && (
        <div>
          <p className="text-xs text-gray-500">Post-Delivery</p>
          <p className="text-sm text-gray-900">
            {getActionLabel(postDeliveryAction?.action)}
            {postDeliveryAction?.archivePath && (
              <span className="text-gray-600"> → {postDeliveryAction.archivePath}</span>
            )}
          </p>
        </div>
      )}
    </div>
  );
};
